import { keys, every, isUndefined, assignWith, identity } from "lodash";
import { FunctionId, ObjectId, UnionId, TypedArrayId, FormatTypeId, typeUnionId, IdError, SingletonId, UntypedArrayId } from "../../../typing/typing";

/**
 * List External object type.
 *
 * List is a way to deal with lists (sequence of elements) in Lambda.
 * so it might be considered as an example of External object implementation.
 * It is implemented as External object,
 *
 * A **List term** is defined as an JS object with mandatory string field `type`
 * which determines object's structure and semantic.
 * Unlike Lambda, List terms might not be created with constructors defined
 * in this module, they are referential equivalent
 * (i.e. each List term is semantically equivalent to its top-level clone).
 *
 * The following types are defined:
 * - `"List"` -- explicit finite list of elements
 * - `"Map"` -- map a lambda term to a list
 *
 * Note that a list term may be considered as an expression to evaluate.
 * In this version, it includes two kinds of evaluation:
 * - elements are lambda terms,
 * - Map term holds a function to be applied to each element of a list.
 *
 * A **List object** is an object `new External(term, ListMarker)` where
 * `term` is a List term. Each List object is hence a lambda term with
 * `objType = ListMarker`. By default, `ListMarker = "List"`.
 *
 * @module EInstances/Lists
*/

import { LambdaTermId, Constructors, isLambdaTerm, unboxExternal, isExternal, reduceLambdaObject, wrapToObject, isLambdaObject, reduce as _reduce, Reduction, LambdaObjectId, ExternalObjectInterpretation } from "../Lambda";
import Types from "../Types";
const { AnyType, compare, BasicType, compatible, ModusPonens } = Types;

/** ListId. Use `ListId` to verify inner structure of an object. */
export function ListId(term) {
  ObjectId(term);
  switch(term.type) {
    case "List":
      return FormatTypeId({
          // elType: StringId,
          list: TypedArrayId(LambdaTermId)
        })(term);
    case "Map":
      return FormatTypeId({
          func: LambdaTermId,
          list: typeUnionId(ListId, LambdaTermId)
        })(term);
  }
  throw new IdError("unknown type", term, ListId);
}

ListId.typeName = "List term";

/**
 * A **List term** is defined as an JS object
 * with mandatory string field `type` which value is of predefine finite set:
 * "List" or "Map".
 * @typedef ListTerm
 * @global
 */

/**
 * Use `isListTerm` to fast check `type` property.
 * @see module:Lists~ListId
 * @param {*} obj - any JS value
 * @return true if obj has attributes of a list term, false otherwise
 */
export function isListTerm(obj) {
  return obj && (obj.type == "List" || obj.type == "Map");
}

/**
 * test `obj` to be an object with `type == ListMarker` and `list` value
 * that has list term's attributes.
 * @param {*} obj - any JS value
 * @return true if obj has attributes of a list object, false otherwise
 * @see module:EInstances/Lists~ListMarker
 */
export function isListObject(obj) {
  return obj && obj.type == ListMarker && isListTerm(obj.list);
}

/**
 * a string using as `objType` of External term for List objects.
 */
export const ListMarker = "List";

/** ListObjectId. */
export const ListObjectId = FormatTypeId({
    type: SingletonId(ListMarker),
    list: ListId
  }, "List object");

/**
 * Basic constructor of finite list of lambda terms.
 * @param {LambdaTerm[]} list - an array of lambda terms (not lambda objects!)
 * @param {Type} [elType] - a type of each element
 * @return {ListTerm}
 */
export function List(list, elType) {
  if(keys(this).length > 0) return new List(list, elType);
  this.type = "List";
  this.list = UntypedArrayId(list);
  this.elType = elType;
}

/**
 * Another constructor of finite list.
 * Unlike List, elements of PureList's array are just values, not lambda terms.
 * @param {Array} list - an array of values
 * @param {Type} elType - a type of each element
 * @return {ListTerm} - a List term of "List" type
 *   with array's elements wrapped with `External` constructor.
 */
export function PureList(list, elType) {
  if(elType === undefined)
    throw "PureList: elType must be specified";
  return new List(
           UntypedArrayId(list)
            .map(function(x) {
                     return new Constructors.External(x, elType);
                   }),
           elType);
}

/**
 * `Map(func, list)` represents a `list` with elements being mapped with `func`.
 * @param {LambdaTerm} func - a lambda term, likely of functional type
 * @param {ListTerm} list - list term
 * @return {ListTerm} - a List term of type "Map".
 */
export function Map(func, list) {
  if(keys(this).length > 0) return new Map(func, list);
  if(!isLambdaTerm(func)) {
    console.error("a function in List Map is not a lambda term");
    console.warn(func);
    throw "a function in List Map is not a lambda term";
  }
  if(!isListTerm(list) && !isLambdaTerm(list)) {
    console.error("an argument of List Map is not a list term");
    console.warn(list);
    throw "an argument of List Map is not a list term";
  }
  this.type = "Map";
  this.func = func;
  this.list = list;
}

/***    TYPING    ***/

/**
 * Constructor of a list type with given element type.
 * @param {Type} elType - a type of each element.
                   Use undefined as an alias for AnyType.
 * @return {Type} - lambda type of List object
 */
export function ListType(elType) {
  if(keys(this).length > 0) return new ListType(elType);
  this.elType = elType || AnyType;
}

ListType.prototype = {
  eq: function(t) {
    return (t instanceof ListType) && compare(this.elType, t.elType);
  },
  compat: function(t) {
    return t === AnyType ||
      (t == "List") ||
      ((t instanceof BasicType) && t.name == "List") ||
      ((t instanceof ListType) && compatible(this.elType, t.elType));
  }
};

/**
 * derive lambda type of elements.
 * @param {ListTerm} e - List term
 * @return {Type} lambda type of list's elements
 * @private
 */
function elementType(e) {
  ListId(e);
  if(e.type == "List") {
    if(e.list.length == 0) {
      return e.elType || AnyType;
    } else {
      if(e.elType) return e.elType;
      return e.elType = e.list[0].type();
    }
  }
  if(e.type == "Map") {
    var ft = e.func.type();
    var lt = e.list.type ? e.list.type() : elementType(e.list);
    if(lt == AnyType) {
      console.error("List element type is AnyType");
      console.warn(e.list);
    }
    return ModusPonens(ft, lt);
  }
  throw "unknown type";
}

/**
 * Lambda type of given list.
 * @param {ListTerm} list - list term
 * @return {Type}
 * @private
 */
function getType(list) {
  switch(list.type) {
    case "List":
      return new ListType(elementType(list));
    case "Map":
      return new ListType(
        ModusPonens(
          getType(list.func), // function applying to each element
          elementType(list.list)
        ));
  }
  return new ListType();
}

/**
 * converts a list term to External lambda term with inferred lambda type.
 * @param {ListTerm} list - a list term
 * @return External lambda term
 * @see module:EInstances/Lists~ListMarker
 */
export function toExternal(list) {
  try {
    return new Constructors.External(list, ListMarker, getType(list));
  } catch(e) {
    console.error("List.toExternal: Could not derive a type of the list");
    console.error(list);
  }
}


/***    REDUCTION    ***/

/**
 * ListTermReduction is a reduction of a List term.
 * 
 * ListTermReduction do 2 main things:
 * 1. it applies given `reduce` function to each element
 * 2. it do some general reductions based on semantic of non-basic constructors
 *   e.g. `Map` is transformed to `List` whose elements are `Application`.
 * 
 * @example
 * <caption>List term reduction with no lambda subterm reduction</caption>
 * ListTermReduction(term, function(){})
 *
 * @param {ListTerm} term - a list term
 * @param {LambdaReduction} reduce - a reduction of lambda terms
 * @return
 *   - reduced lambda term
 *   - `undefined` if `term` contains no redex
 */
export function ListTermReduction(term, reduce) {
  
  function NoReduction() {}
  function reduce0(term) { return reduce(term, NoReduction); }
  function defaultVal(a, b) { return a === undefined ? b : a; }
  
  switch(term.type) {
    case "Map":
      var func = reduce0(term.func);
      if(func !== undefined) return Map(func, term.list);      
      term.list = unboxExternal(term.list, "List") || term.list;
      var list = isLambdaTerm(term.list)
               ? reduce(term.list)
               : ListTermReduction(term.list, reduce);
      if(list !== undefined) return Map(term.func, list);
      func = term.func;
      list = term.list;
      var lelType = isLambdaTerm(list) ? list.type() : elementType(list);
      var elType = ModusPonens(func.type(), lelType);
      if(elType == AnyType) {
        console.error("List element type is AnyType");
        console.warn(list);
      }
      if(list.type == "List")
        return List(list.list.map(function(x) {
            return new Constructors.Application(func, x);
          }), elType);
      return undefined;
    case "List":
      var list = term.list.map(reduce0);
      if(every(list, isUndefined)) return undefined;
      // replace undefined elements of list with origin terms of term.list
      assignWith(list, term.list, defaultVal);
      return List(list, term.elType);
  }
  
  console.error(term);
  throw "unknown list term type " + term.type;
}

/**
 * ListReduction is a reduction (see Lambda) that involves only lambda objects.
 * It invokes ListTermReduction to reduce lambda objects of list terms.
 *
 * When `Lists` module is loaded, ListReduction is added to
 *   the array of default reductions.
 * @param {LambdaTerm} term - lambda term
 * @param {LambdaReduction} reduce - a reduction
 * @return lambda term
 */
export function ListReduction(term, reduce) {
  if(isExternal(term, ListMarker)) {
    var r = ListTermReduction(term.object, reduce);
    if(r === undefined) return undefined;
    return new Constructors.External(r, ListMarker);
  }
}

/**
 * Functor structure of List terms.
 * Use it as an implementation of ExternalObjectInterpretation's `map` function.
 * `ListMap(obj, f)` applies `f` to every lambda subterm of a list term `obj`.
 * 
 * This function is exported to `ExternalObjectInterpretation[ListMarker].map`.
 * @param obj - a list term
 * @param f - a function of lambda term
 * @return a list term with all lambda subterms mapped with `f`.
 * @private
 */
export function ListMap(obj, f) {
  // ListId(obj);
  switch(obj.type) {
    case "List":
      return { type: "List", list: obj.list.map(f), elType: obj.elType };
    case "Map":
      var mfunc = f(obj.func);
      var mlist = isLambdaTerm(obj.list)
                ? f(obj.list)
                : ListMap(obj.list, f);
      return { type: "Map", func: mfunc, list: mlist };
    default:
      return obj;
  }
}

/**
 * Reduce lambda term representing a list to a list term.
 * @see module:EInstances/Lists~ListToArray
 * @param {LambdaTerm} term - lambda term
 * @return {ListTerm}
 * @throws when term cannot be reduced to External List object
 * @private
 */
function reduceLambdaTermToListTerm(term) {
  var obj = reduceLambdaObject({ type: "Lambda", term: term });
  if(!isListTerm(obj)) {
    var msg = "Cannot reduce non-external lambda term to list term";
    console.error(msg);
    console.warn(obj);
    throw msg;
  }
  return obj;
}

reduceLambdaTermToListTerm = FunctionId(reduceLambdaTermToListTerm,
  LambdaTermId, ListId);

/**
 * reduce lambda term to External term and return unwrapped object.
 * @see module:EInstances/Lists~ListToArray
 * @param {LambdaTerm} e - lambda term
 * @return JS object, unwrapped External object
 * @throws when lambda term cannot be reduced to External object
 * @private
 */
function reduceListElement(e) {
  var obj = wrapToObject(e);
  if(isLambdaObject(obj)) {
    var r = reduceLambdaObject(obj);
    if(isLambdaObject(r)) {
      console.error("Could not reduce lambda term to external object");
      console.error(r);
      throw "Could not reduce lambda term to external object";
    }
    return r;
  } else return obj;
}

reduceListElement = FunctionId(reduceListElement,
  LambdaTermId, identity);

/**
 * check whether given list term contains External elements.
 * This function does not cause list/lambda evaluation.
 * Formally, `isPureList` does not correlate with normal forms:
 *   1. if an element an unassigned variable, a list might be in NF,
 *      but `isPureList` returns `false`,
 *   2. External terms might not be in NF.
 * However, if `isPureList` returns `true`, a term is in normal form
 * in sence of list term reductions, see {@link module:Lists~ListTermReduction}
 * @param term - a list term
 * @param elType - a type (lambda type or string) of each element (optional)
 * @return boolean
 */
export function isPureList(term, elType) {
  switch(ListId(term).type) {
    case "List":
      return every(term.list,
        function(t) { return isExternal(t, elType); });
  }
  return false;
}


/**
 * ListToArray(obj) forces a list object `obj` to be reduced
 *   to List of External objects.
 * An array of unwrapped External objects is returned.
 * That is, given a complex List/Lambda expression that 
 *   refers to a list of numbers, ListToArray converts it to an array of numbers
 * @param {LambdaTerm} obj - list object or lambda term
 * @return an array of objects (likely, neither Lambda nor List)
 * @throws when neither List object nor Lambda term is given
 * @throws when Lambda term can not be reduced to a List object
 * @throws when List term can not be reduced to a List term of "List" type
 * @throws when List term is not a finite list of External terms
 */
export function ListToArray(obj) {
  // list - a list term
  var term = null;
  if(isListObject(obj)) term = obj.list;
  if(isLambdaTerm(obj))
    term = reduceLambdaTermToListTerm(obj);
  if(term === null) {
    console.error(obj);
    throw "Cannot convert list to array - unknown format"
  }
  if(!isListTerm(term)) {
    console.error(term);
    throw "not a list term";
  }
  // now the list term is extracted, so it can be reduced to its normal form
  term = FullListTermReduce(term, _reduce);
  if(term.type !== "List") {
    console.error("Cannot reduce a list term to primitive List type");
    console.error(term);
    throw "Cannot reduce a list term to primitive List type";
  }
  return term.list.map(reduceListElement);
}

ListToArray = FunctionId(ListToArray,
  UnionId([ListObjectId, LambdaTermId]),
  UntypedArrayId);

/**
 * reduce a List term to its normal form
 *   with {@link module:EInstances/Lists~ListTermReduction}
 *   and nested lambda term reduction.
 * @param {ListTerm} term - a list term to reduce
 * @param {LambdaReduction} lreduce - lambda term reduction
 * @return a list term in normal form
 * @private
 */
function FullListTermReduce(term, lreduce) {
  function noReduction(){}
  function specListReduction(t) {
    if(t.type == "Map" && isLambdaTerm(t.list)) {
      var lr = lreduce(t.list, noReduction);
      if(lr !== undefined) {
        return new Map(t.func, lr);
      }
    }
    return ListTermReduction(t, lreduce);
  }
  ListId(term);
  var reduce = Reduction.ManyStepReduction(specListReduction);
  var t = reduce(term);
  if(t === undefined) return term;
  return t;
}

/**
 * List-like object is an array, a List object or a Lambda object.
 * @typedef Listlike
 * @global
 */

/*
 * Use `ListlikeId(element)` to create an identification of list-like object
 *   with elements of given type
 * @param {Id} an identification function of element's type
 * @return an identification function of list-list object
 */
export function ListlikeId(element) {
  var elementTypeName = element.typeName || '<...>';
  return UnionId([
      TypedArrayId(element),
      ListObjectId,
      LambdaObjectId
    ], "list-like object of " + elementTypeName);
}

/**
 * convert list-like object to an array.
 *
 * This function is similar to `ListToArray`: both process List objects
 *   and Lambda terms that can be reduced to External List objects,
 *   and both functions enforce full reduction.
 *
 * There are two key points they differs at:
 *   1. Lambda object is List-like, lambda term is not.
 *      Meanwhile `ListToArray` processes Lambda terms, but not Lambda objects.
 *   2. Arrays are list-like, `ListlikeToArray` does not affect them,
 *      while `ListToArray` fails when its argument is an array.
 * @see module:EInstances/Lists~ListlikeId
 * @param {Listlike} list - a list-like object
 *   (an array, a lambda object or a list object)
 * @return an array of elements' values.
 * @throws an error if non-array list cannot be reduced
 */
export function ListlikeToArray(obj) {
  if(obj instanceof Array)
    return obj;
  if(isListObject(obj))
    return ListToArray(obj);
  if(isLambdaObject(obj))
    return ListToArray(obj.term);
}


export function ListToJSON(obj, prefered, forbidden, lambdaExport) {
  // lambdaExport closure
  function ltExport(element) {
    return lambdaExport(element, prefered, forbidden, lambdaExport);
  }
  var json = { type: obj.type };
  switch(obj.type) {
    case "List":
      json.list = obj.list.map(ltExport);
      json.elType = obj.elType;
      return json;
    case "Map":
      json.func = ltExport(obj.func);
      json.list = ListToJSON(obj.list, prefered, forbidden, lambdaExport);
      return json;
  }
  throw "ListToJSON: unknown type";
}

export function JSONToList(json, env, lambdaImport) {
  // lambdaImport closure
  function ltImport(element) {
    return lambdaImport(element, env, lambdaImport);
  }
  var res = { type: json.type };
  switch(json.type) {
    case "List":
      res.list = json.list.map(ltImport);
      res.elType = json.elType;
      return res;
    case "Map":
      res.func = ltImport(json.func);
      res.list = JSONToList(json.list, env, lambdaImport);
      return res;
  }
  throw "JSONToList: unknown type";
}


/***    COMBINATION LIST AND LAMBDA    ***/

ExternalObjectInterpretation[ListMarker] = {
  map: ListMap,
  traverse: undefined,
  importFromJSON: JSONToList,
  exportToJSON: ListToJSON,
  id: ListId
};

// Lambda.Reduction already contains ExternalReduction
//   which applies a reduction to each subterm of List term using ListMap
// ListReduction provides sematic of Map/etc
Reduction.DefaultReductions.push(ListReduction);
Reduction.DefaultReductions.push(Reduction.ExternalReduction("List"));

Types.ListType = ListType;
